import adserver from 'services/adserver';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { proposalsKeys } from './proposals.keys';
import {
  MaterialsParams,
  MaterialFileParams,
  MaterialLinkParams,
} from './proposals.types';
import { normalizeDateFromApiWithoutTimeCorrection } from 'helpers/datetimeHelpers';

export type NormalizedMaterialFileParams = Omit<
  MaterialFileParams,
  'uploadDate'
> & {
  uploadDate: Date;
};

export type NormalizedMaterialLinkParams = Omit<
  MaterialLinkParams,
  'uploadDate'
> & {
  uploadDate: Date;
};

export type NormalizedMaterialsParams = Omit<
  MaterialsParams,
  'files' | 'links'
> & {
  files: NormalizedMaterialFileParams[];
  links: NormalizedMaterialLinkParams[];
};

const normalizeApiResponse = (
  data: MaterialsParams,
): NormalizedMaterialsParams => ({
  ...data,
  files: data.files.map(file => ({
    ...file,
    uploadDate: normalizeDateFromApiWithoutTimeCorrection(file.uploadDate),
  })),
  links: data.links.map(link => ({
    ...link,
    uploadDate: normalizeDateFromApiWithoutTimeCorrection(link.uploadDate),
  })),
});

const getMaterials = async (id: number): Promise<NormalizedMaterialsParams> => {
  const { data } = await adserver({
    url: `/proposal/${id}/materials`,
    headers: {},
  });

  return normalizeApiResponse(data);
};

export const useGetMaterials = <TData = NormalizedMaterialsParams>(
  id: number,
  select?: (data: NormalizedMaterialsParams) => TData,
): UseQueryResult<TData, unknown> => {
  return useQuery({
    queryKey: proposalsKeys.materials(id),
    queryFn: () => getMaterials(id),
    enabled: !!id,
    select,
  });
};
